// Utility: Indian number formatting and EMI calculation

/**
 * Format a number using Indian number system (lakhs, crores)
 * e.g. 2850000 → ₹28,50,000
 */
export function formatIndianCurrency(amount: number): string {
  if (isNaN(amount)) return '₹0';
  const rounded = Math.round(amount);
  const str = rounded.toString();
  if (str.length <= 3) return '₹' + str;

  // Indian formatting: last 3 digits, then groups of 2
  const lastThree = str.slice(-3);
  const rest = str.slice(0, str.length - 3);
  const formatted = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return '₹' + (rest ? formatted + ',' + lastThree : lastThree);
}

/**
 * Abbreviated Indian currency for large values
 * e.g. 2850000 → ₹28.5L, 10000000 → ₹1Cr
 */
export function formatIndianShort(amount: number): string {
  if (amount >= 10000000) {
    return '₹' + (amount / 10000000).toFixed(1).replace(/\.0$/, '') + 'Cr';
  }
  if (amount >= 100000) {
    return '₹' + (amount / 100000).toFixed(1).replace(/\.0$/, '') + 'L';
  }
  return formatIndianCurrency(amount);
}

/**
 * Calculate monthly EMI using standard reducing balance formula
 * EMI = P × r × (1+r)^n / ((1+r)^n - 1)
 * Verified: ₹10,00,000 @ 8.5% for 240 months = ₹8,678/month
 *           ₹5,00,000 @ 10% for 60 months = ₹10,624/month
 */
export function calculateEMI(
  principal: number,
  annualRate: number,
  tenureMonths: number
): number {
  if (principal <= 0 || annualRate <= 0 || tenureMonths <= 0) return 0;
  const r = annualRate / 12 / 100;
  const n = tenureMonths;
  const emi = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Math.round(emi);
}

/**
 * Generate full amortization schedule month by month
 */
export interface AmortizationRow {
  month: number;
  monthLabel: string;
  openingBalance: number;
  emi: number;
  principal: number;
  interest: number;
  closingBalance: number;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function generateAmortization(
  principal: number,
  annualRate: number,
  tenureMonths: number,
  startDate: Date
): AmortizationRow[] {
  const emi = calculateEMI(principal, annualRate, tenureMonths);
  const r = annualRate / 12 / 100;
  const rows: AmortizationRow[] = [];
  let balance = principal;

  for (let i = 1; i <= tenureMonths; i++) {
    const interest = Math.round(balance * r);
    const principalPart = Math.round(emi - interest);
    const closing = Math.max(0, balance - principalPart);

    const d = new Date(startDate);
    d.setMonth(d.getMonth() + (i - 1));
    // Deterministic month label — no locale dependency
    const monthLabel = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;

    rows.push({
      month: i,
      monthLabel,
      openingBalance: Math.round(balance),
      emi,
      principal: principalPart,
      interest,
      closingBalance: closing,
    });

    balance = closing;
    if (balance <= 0) break;
  }

  return rows;
}

/**
 * Format date as DD/MM/YYYY — deterministic, no locale dependency
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format date as "15 Apr 2025" — deterministic, no locale dependency
 */
export function formatDateLong(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return `${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Days until a date from today
 */
export function daysUntil(date: Date | string): number {
  const d = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Get loan type badge class
 */
export function getLoanTypeBadgeClass(type: string): string {
  const map: Record<string, string> = {
    Home: 'badge badge-home',
    Car: 'badge badge-car',
    Personal: 'badge badge-personal',
    'Credit Card': 'badge badge-credit',
    Phone: 'badge badge-phone',
    Other: 'badge badge-other',
  };
  return map[type] || 'badge badge-other';
}

/**
 * Get status badge class
 */
export function getStatusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    Paid: 'badge status-paid',
    'Due soon': 'badge status-due-soon',
    Overdue: 'badge status-overdue',
    Upcoming: 'badge status-upcoming',
  };
  return map[status] || 'badge status-upcoming';
}

/**
 * Determine EMI status for a given due date
 */
export function getEMIStatus(dueDateStr: string, isPaid: boolean): string {
  if (isPaid) return 'Paid';
  const days = daysUntil(dueDateStr);
  if (days < 0) return 'Overdue';
  if (days <= 7) return 'Due soon';
  return 'Upcoming';
}